<template>
  <div
    ref="PostWrapper"
    :to="link"
    class="war-room-post-preview-presenter"
    :class="{ 'war-room-post-preview-presenter--pinned': post.pinned }"
  >
    <div :class="ratingClass" data-test="post-preview-rating">
      <UpvoteIcon width="16" height="16" :class="upvoteClass" data-test="post_preview_vote_up" @click.stop="onUpvote" />
      {{ post.rating }}
      <DownvoteIcon
        width="16"
        height="16"
        :class="downvoteClass"
        data-test="post_preview_vote_down"
        @click.stop="onDownvote"
      />
    </div>
    <div class="war-room-post-preview-presenter__info-container">
      <WarRoomMembersOnlyLabel
        v-if="post.members_only && !$auth.loggedIn"
        class="war-room-post-preview-presenter__members-label-row"
      />
      <div class="war-room-post-preview-presenter__info-row">
        <div class="war-room-post-preview-presenter__info">
          <NuxtLink :to="link" data-test="post_preview_title" :class="titleClass">
            <span v-if="isUnreadIconVisible" class="war-room-post-preview-presenter__new-post-icon" />
            <div v-if="post.pinned">
              <PinnedIcon width="18" height="18" class="war-room-post-preview-presenter__pinned-icon" />
            </div>
            <template v-if="isDealStory">
              <div class="war-room-post-preview-presenter__deal-header">
                <BravadoText variant="caption1-semibold">
                  <DealStoryBadge />
                </BravadoText>
              </div>
              <div class="war-room-post-preview-presenter__deal-content">
                <div class="war-room-post-preview-presenter__deal-story">
                  <div class="war-room-post-preview-presenter__avatar-wrapper">
                    <ClientOnly>
                      <WarRoomAvatar :title="post.user_username" type="tiny" :src="dealAuthorAvatar" />
                    </ClientOnly>
                  </div>
                  <BravadoText variant="headline semibold">
                    <WarRoomDealStoryHeaderFormatter
                      :author="post.user_username"
                      :company="dealCompanyName"
                      :amount="post.content.data.deal.amount"
                      :departments="dealDepartments"
                      :with-author-link="false"
                      secondary-color="var(--theme-color-sub-text)"
                    />
                  </BravadoText>
                  <BravadoText variant="body" class="war-room-post-preview-presenter__deal-story-preview">
                    <div v-html="post.content.data.preview" />
                  </BravadoText>
                </div>
                <div v-if="hasPreviewImage">
                  <WarRoomPostPreviewImage :post="post" />
                </div>
              </div>
            </template>
            <BravadoText
              v-else
              tag="h2"
              variant="headline-semibold"
              class="war-room-post-preview-presenter__post-title"
            >
              {{ post.content.data.title }}
            </BravadoText>
          </NuxtLink>
          <div class="war-room-post-preview-presenter__tags">
            <WarRoomCategoryBadge
              v-if="!isDealStory"
              :post="post"
              data-test="post_preview_topic"
              class="war-room-post-preview-presenter__topic"
            />
            <WarRoomCategoryBadgeTopic
              v-if="post.topics.length"
              :topics="post.topics"
              class="war-room-post-preview-presenter__topic"
            />
            <NuxtLink v-slot="{ navigate }" :to="commentsButtonLink" custom>
              <div class="war-room-post-preview-presenter__tag" @click="navigate">
                <CommentIcon width="16" height="16" class="war-room-post-preview-presenter__icon" />
                {{ commentsButtonText }}
                <div v-if="post.comment_count.new" class="war-room-post-preview-presenter__new-comments">
                  +{{ post.comment_count.new }}
                </div>
              </div>
            </NuxtLink>
            <div
              v-if="isDealStory && (post.commenters || []).length"
              class="war-room-post-preview-presenter__commenters"
            >
              <template v-for="commenter in post.commenters">
                <WarRoomAvatar
                  v-if="isPublicCommenter(commenter)"
                  :key="commenter.slug"
                  type="next-medium-tiny"
                  :src="commenter.avatar_url"
                  :title="commenter.full_name.substring(0, 2)"
                  class="war-room-post-preview-presenter__commenter"
                />
                <WarRoomAvatar
                  v-else
                  :key="commenter.username"
                  type="next-medium-tiny"
                  :src="commenter.war_room_avatar_url"
                  :title="commenter.username.substring(0, 2)"
                  class="war-room-post-preview-presenter__commenter"
                />
              </template>
              <div v-if="post.comment_count.total > 3" class="war-room-post-preview-presenter__extra-commenters-count">
                <BravadoText tag="span" variant="caption3 semibold">+{{ post.comment_count.total - 3 }}</BravadoText>
              </div>
            </div>
          </div>
        </div>
        <div v-if="hasPreviewImage && !isDealStory">
          <WarRoomPostPreviewImage :post="post" />
        </div>
        <WarRoomPostActions v-if="isAdmin" :post="post" class-name="war-room-post-preview-presenter__menu" />
      </div>
      <div class="war-room-post-preview-presenter__info-row">
        <WarRoomPostPollV2 v-if="post.poll" :post="post" :class="pollClasses" @vote="$emit('on-poll-vote', $event)" />
        <WarRoomExposedComments v-if="false && showExposedComments" :post="post" />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import type { PropOptions } from 'vue';
import Vue from 'vue';
import { mapGetters } from 'vuex';
import PinnedIcon from './images/pinned.vue.svg';
import CommentIcon from './images/preview-comment.vue.svg';
import DownvoteIcon from './images/preview-downvote.vue.svg';
import UpvoteIcon from './images/preview-upvote.vue.svg';
import WarRoomPostActions from '@/components/partials/war-room/WarRoomPostActions.vue';
import WarRoomPostPreviewImage from '@/components/partials/war-room/WarRoomPostPreviewImage/WarRoomPostPreviewImage.vue';
import type { ICommenter, IPost, IPostCategory } from '@/types/community';
import WarRoomCategoryBadge from '@/components/partials/war-room/WarRoomCategoryBadge/WarRoomCategoryBadge.vue';
import WarRoomCategoryBadgeTopic from '@/components/partials/war-room/WarRoomCategoryBadge/WarRoomCategoryBadgeTopic.vue';
import WarRoomPostPollV2 from '@/components/partials/war-room/WarRoomPostPollV2/WarRoomPostPollV2.vue';
import WarRoomExposedComments from '@/components/partials/war-room/WarRoomExposedComments/WarRoomExposedComments.vue';
import WarRoomMembersOnlyLabel from '~/components/partials/war-room/WarRoomMembersOnlyLabel/WarRoomMembersOnlyLabel.vue';
import { authorTypes } from '~/components/partials/war-room/constants';
import warRoomSignUpMixin from '~/components/partials/war-room/WarRoomSignUp/warRoomSignUpMixin';
import BravadoText from '~/components/common/BravadoText/BravadoText.vue';
import { categoryNames } from '~/components/partials/war-room/WarRoomPostForm/WarRoomPostFormCategories/constants';
import DealStoryBadge from '~/components/partials/war-room/WarRoomDeal/DealStoryBadge.vue';
import WarRoomDealStoryHeaderFormatter from '~/components/partials/war-room/WarRoomDeal/WarRoomDealStoryHeaderFormatter.vue';
import WarRoomAvatar from '~/components/partials/war-room/WarRoomAvatar.vue';
import { isPostContentADealStory } from '~/types/typeHelpers';

export default Vue.extend({
  name: 'WarRoomPostPreviewPresenter',
  components: {
    WarRoomAvatar,
    WarRoomDealStoryHeaderFormatter,
    DealStoryBadge,
    UpvoteIcon,
    DownvoteIcon,
    CommentIcon,
    BravadoText,
    WarRoomPostActions,
    WarRoomPostPreviewImage,
    PinnedIcon,
    WarRoomCategoryBadge,
    WarRoomCategoryBadgeTopic,
    WarRoomPostPollV2,
    WarRoomExposedComments,
    WarRoomMembersOnlyLabel,
  },
  mixins: [warRoomSignUpMixin],
  props: {
    post: {
      type: Object,
      required: true,
    } as PropOptions<IPost>,
  },
  computed: {
    ...mapGetters('warRoom', ['isAdmin']),
    titleClass(): Record<string, boolean> {
      return {
        'war-room-post-preview-presenter__title': true,
        'war-room-post-preview-presenter__title--deal': this.isDealStory,
      };
    },
    showExposedComments(): boolean {
      if (this.post.poll) return false;

      return !!Object.keys(this.post.popular_comments?.comment_dict || {}).length;
    },
    link(): string {
      return `${this.$pages.POSTS_PAGE.path}/${this.post.slug}`;
    },
    isUpvoted(): boolean {
      return this.post.current_vote === 1;
    },
    isDownvoted(): boolean {
      return this.post.current_vote === -1;
    },
    ratingClass(): Record<string, boolean> {
      return {
        'war-room-post-preview-presenter__rating': true,
        'war-room-post-preview-presenter__rating--upvoted': this.isUpvoted,
        'war-room-post-preview-presenter__rating--downvoted': this.isDownvoted,
      };
    },
    upvoteClass(): Record<string, boolean> {
      return {
        'war-room-post-preview-presenter__rate': true,
        'war-room-post-preview-presenter__rate--upvoted': this.isUpvoted,
      };
    },
    downvoteClass(): Record<string, boolean> {
      return {
        'war-room-post-preview-presenter__rate': true,
        'war-room-post-preview-presenter__rate--downvoted': this.isDownvoted,
      };
    },
    hasPreviewImage(): boolean {
      return Boolean(this.post.cover_url);
    },
    isUnreadIconVisible(): boolean {
      if (this.post.pinned) return false;
      if (!this.$auth.loggedIn) return false;
      if (this.isDealStory) return false;
      if (this.$auth.user.username === this.post.user_username) return false;

      return this.post.viewed === false;
    },
    pollClasses(): Record<string, boolean> {
      return {
        'war-room-post-preview-presenter__poll': true,
        'war-room-post-preview-presenter__poll--not-logged': !this.$auth.loggedIn,
      };
    },
    isDealStory(): boolean {
      return (this.post.category as IPostCategory).slug === categoryNames.WIN_WIRE;
    },
    commentsButtonText(): string | number {
      if (this.isDealStory) {
        return 'Say congrats!';
      } else if (!this.post.comment_count.total) {
        return 'Comment';
      } else {
        return this.post.comment_count.total;
      }
    },
    commentsButtonLink(): string {
      const linkParams = ['comment=true'];
      if (this.isDealStory) linkParams.push('mention=true');

      const link = `${this.link}?${linkParams.join('&')}`;

      return this.$auth.loggedIn ? link : `?dynamic_modal=${this.$constants.dynamicModals.SIGN_UP_MODAL}`;
    },
    dealCompanyName(): string | undefined {
      if (isPostContentADealStory(this.post.content.data) && this.post.content.data.deal.company) {
        return this.post.content.data.deal.company.name;
      } else {
        return undefined;
      }
    },
    dealDepartments(): string[] {
      if (isPostContentADealStory(this.post.content.data) && this.post.content.data.deal.departments) {
        return this.post.content.data.deal.departments.map(d => d.name);
      } else {
        return [];
      }
    },
    dealAuthorAvatar(): string {
      return isPostContentADealStory(this.post.content.data)
        ? this.post.content.data.deal.user.war_room_avatar_url
        : '';
    },
  },
  methods: {
    isPublicCommenter(commenter: ICommenter): boolean {
      return commenter.type === authorTypes.PUBLIC;
    },
    onUpvote() {
      if (!this.$auth.loggedIn) {
        // @ts-ignore, method comes from mixin
        this.openWarRoomSignUpModal();
        return;
      }

      if (this.isUpvoted) {
        this.$emit('on-remove-vote');
      } else {
        this.$emit('on-add-vote', 1);
      }
    },
    onDownvote() {
      if (!this.$auth.loggedIn) {
        // @ts-ignore, method comes from mixin
        this.openWarRoomSignUpModal();
        return;
      }

      if (this.isDownvoted) {
        this.$emit('on-remove-vote');
      } else {
        this.$emit('on-add-vote', -1);
      }
    },
  },
});
</script>

<style scoped lang="scss">
.war-room-post-preview-presenter {
  padding: 10px 10px 10px 12px;
  background: var(--theme-color-card);
  box-shadow: var(--theme-elevation-01);
  transition: box-shadow 0.3s ease-in-out;
  border-radius: 8px;
  display: flex;
  align-items: flex-start;
  position: relative;

  &--pinned {
    background: var(--theme-color-highlight);
    border: 1px solid var(--gold-50);
  }

  &__container {
    display: flex;
    align-items: flex-start;
    position: relative;
  }

  &__link {
    text-decoration: none;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1;
  }

  &__rating {
    @include subhead-semibold;

    margin-right: 16px;
    flex-shrink: 0;
    color: var(--theme-color-text);
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
    z-index: 2;

    &--upvoted {
      color: var(--blue-60);
    }

    &--downvoted {
      color: var(--red-50);
    }
  }

  &__rate {
    cursor: pointer;
    color: var(--theme-color-text);
    opacity: 0.3;
    transition: opacity 0.2s;

    &--upvoted {
      color: var(--blue-60);
      opacity: 1;
    }

    &--downvoted {
      color: var(--red-50);
      opacity: 1;
    }

    &:hover {
      opacity: 1;
    }
  }

  &__info-row {
    display: flex;
  }

  &__members-label-row {
    margin-bottom: 8px;
  }

  &__info-container {
    flex: 1;
    width: calc(100% - 32px);
  }

  &__info {
    flex: 1;
  }

  &__new-post-icon {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--blue-60);
    display: inline-block;
    margin-right: 8px;
    flex-shrink: 0;
    margin-top: 10px;
  }

  &__title {
    display: flex;
    color: var(--theme-color-text);
    text-decoration: none;

    &--deal {
      flex-direction: column;
    }
  }

  &__post-title {
    word-break: break-word;
  }

  &__tags {
    display: inline-flex;
    align-items: center;
    position: relative;
    z-index: 3;
    flex-wrap: wrap;
  }

  &__tag-text {
    @include subhead-semibold;

    color: var(--black);
    opacity: 0.7;
  }

  &__tag {
    @include subhead-semibold;

    text-decoration: none;
    display: flex;
    align-items: center;
    margin-top: 8px;
    margin-right: 8px;
    padding: 2px 5px;
    color: var(--theme-color-sub-text);
    border-radius: 4px;
    border: 1px solid var(--theme-color-border);
    cursor: pointer;
    transition: border 0.3s, color 0.3s;

    &:hover {
      color: var(--theme-sub_text-hover);
      border: 1px solid var(--theme-border-hover);
    }
  }

  &__topic {
    margin: 8px 8px 0 0;
  }

  &__icon {
    margin-right: 6px;
  }

  ::v-deep &__menu {
    line-height: 0;
    color: var(--theme-color-sub-text);
    margin: 5px 0 0 10px;
    position: relative;
    z-index: 2;

    &:hover {
      color: var(--theme-sub_text-hover);
    }
  }

  &__pinned-icon {
    margin-right: 3px;
    vertical-align: middle;
    color: var(--gold-50);
  }

  &__new-comments {
    @include caption3;

    color: var(--white);
    margin-left: 6px;
    padding: 0 4px;
    line-height: 16px;
    background: var(--blue-60);
    border-radius: 4px;
  }

  &__deal-header {
    display: flex;
    align-items: center;
    position: relative;
    z-index: 3;
  }

  &__deal-content {
    margin-top: 8px;
    display: flex;
  }

  &__deal-story {
    display: grid;
    grid-template-areas: 'avatar title' 'preview preview';
    grid-template-columns: 22px 1fr;
    grid-column-gap: 4px;
    grid-row-gap: 10px;
    align-items: flex-start;
  }

  &__deal-story-preview {
    grid-area: preview;
  }

  &__avatar-wrapper {
    padding-top: 2px;
  }

  &__deal-avatar {
    margin-right: 4px;
  }

  &__poll {
    margin-top: 10px;
    margin-left: -32px;
    position: relative;
    z-index: 2;
    overflow: hidden;

    @include mq('sm') {
      margin-left: 0;
    }

    &--not-logged {
      pointer-events: none;
    }
  }

  &__commenters {
    display: flex;
    padding-top: 10px;
    align-items: center;
  }

  &__commenter {
    border: 1px solid var(--theme-color-over-card);

    &:not(:first-child) {
      margin-left: -5px;
    }
  }

  &__extra-commenters-count {
    border-radius: 16px;
    border: 1px solid var(--theme-color-over-card);
    background: var(--theme-color-border);
    color: var(--gray-50);
    height: 18px;
    padding: 3px;
    margin-left: -5px;
    z-index: 1;
    position: relative;
    display: flex;
  }
}
</style>
